import {AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {CatastoJobIndicatorComponent} from './catasto-job-indicator.component';
import {NgProgressComponent} from 'ngx-progressbar';
import {Toast} from 'primeng/toast';
import {UtilsService} from '@shared-lib';
import {environment} from '../../../environments/environment';
import {DataStateService, FamigliaStateService, RouteParamsService} from '../service';
import {NavbarComponent} from './navbar/navbar.component';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {Subscription} from 'rxjs';
import {ChatbotIcons} from '../../shared/components/chatbot/interfaces/library.interface';
import {CHATBOT_HISTORY} from '../../shared/constants/app-constants';
import {filter} from 'rxjs/operators';
import {DialogService} from 'primeng/dynamicdialog';
import {ChatbotComponent} from '../../shared/components/chatbot/chatbot.component';
import {SpinnerComponent} from '../../../../projects/shared-lib/src/lib/components/spinner/spinner.component';

@Component({
	selector: 'main',
	imports: [
		NgProgressComponent,
		Toast,
		NavbarComponent,
		RouterOutlet,
		ChatbotComponent,
		SpinnerComponent,
		CatastoJobIndicatorComponent
	],
	template: `
    <ng-progress [spinner]="false" id="progressBar" [color]="PROGRESS_BAR_COLOR" [thick]="true"></ng-progress>
    <p-toast></p-toast>
    <!-- elaborazione visura catastale: resta visibile in ogni pagina finché il lavoro è in corso, a fine
         elaborazione ci si clicca sopra per aprire il raggruppamento -->
    <catasto-job-indicator></catasto-job-indicator>
    <div class="flex flex-column" id="main-container-div">
      <kleros-navbar></kleros-navbar>
      <!--<breadcrumbs class="max-w-full" [class.hidden]="utils.isCell()"></breadcrumbs>-->
      <div class="overflow-y-auto flex-grow-1 relative fadein p-2 main-div-height" id="main-div" (scroll)="onScroll()">
        <spinner></spinner>
        <router-outlet></router-outlet>
        <chatbot [icons]="chatbotIcons"
                 [welcomeMessage]="chatbotLabels.welcomeMessage"
                 [errorMessage]="chatbotLabels.errorMessage"
                 [chatbotStorageKey]="CHATBOT_HISTORY">
        </chatbot>
      </div>
    </div>
	`,
	styles: `
    .main-div-height {
      height: calc(100vh - 45px);
    }
	`
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {

	@ViewChild(NavbarComponent, {read: ElementRef}) navbarRef: ElementRef<HTMLElement>;
	private navbarObserver: ResizeObserver;

	PROGRESS_BAR_COLOR = environment.spinnerColor;

	chatbotIcons: ChatbotIcons = {chatbotIcon: 'assets/img/chatbot/dr_kleros.png', userIcon: 'assets/img/chatbot/question.svg'};
	chatbotLabels: any;
	CHATBOT_HISTORY = CHATBOT_HISTORY;

	subscriptions: Subscription = new Subscription();

	private router = inject(Router);

	constructor(private translate: TranslateService,
	            protected utils: UtilsService,
	            private routeService: RouteParamsService,
	            private state: DataStateService,
	            private famigliaState: FamigliaStateService,
	            private dialogService: DialogService) {
	}

	ngOnInit() {
		this.chatbotLabels = this.translate.instant('chatbot');
		this.subscriptions.add(this.translate.onLangChange.subscribe(() => this.chatbotLabels = this.translate.instant('chatbot')));

		// gestione app attiva:
		this.updateActiveApp(this.routeService.params['app']);

		// gestione navigazione:
		this.subscriptions.add(this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((ne: NavigationEnd) => {
			if (!ne.url.includes('login') && !ne.url.includes('login-crm-demo')) {
				// aggiorno notifiche se non siamo in login e/o pagina di manutenzione...
				this.state.updateNotifiche();
			}
			const appFromURL: string = this.routeService.params['app'];
			this.updateActiveApp(appFromURL);
			const currentState = this.router.currentNavigation()?.extras.state ?? {};
			if (!currentState['showDialog']) {
				// routing (magari anche con avanti/indietro) --> chiusura di tutti i p-dialog eventualmente aperti
				// tranne quando si va in login perché facciamo vedere i dialog gestiti da error-interceptor
				this.dialogService.dialogComponentRefMap.forEach(dialog => dialog.destroy());
			}
		}));
	}

	ngAfterViewInit() {
		// bordo inferiore REALE della navbar (l'altezza cambia con lo schermo: su tablet le voci vanno a capo) come variabile CSS
		// --navbar-altezza: i drawer laterali si aprono sotto la navbar (v. .p-drawer-mask in styles.scss)
		const navbar: HTMLElement = this.navbarRef?.nativeElement?.querySelector('.p-toolbar') ?? this.navbarRef?.nativeElement;
		if (navbar && typeof ResizeObserver !== 'undefined') {
			this.navbarObserver = new ResizeObserver(() =>
				document.documentElement.style.setProperty('--navbar-altezza', Math.round(navbar.getBoundingClientRect().bottom) + 'px'));
			this.navbarObserver.observe(navbar);
		}
	}

	ngOnDestroy() {
		this.navbarObserver?.disconnect();
		document.documentElement.style.removeProperty('--navbar-altezza');
		this.subscriptions.unsubscribe();
	}

	private updateActiveApp(appName?: string) {
		const currentAPP: string = this.state.activeApp;
		if (appName !== currentAPP) {
			// cambiata app --> resetto "cache" def. nodi famiglia
			this.famigliaState.resetAllDefs();
		}
		// aggiorno / resetto active app
		if (appName) {
			this.state.setActiveAppName(appName);
		} else {
			this.state.resetActiveAppName();
		}
	}

	onScroll() {
		requestAnimationFrame(() => {
			this.famigliaState.emitScroll();
		});
	}

}
